import { useCallback, useEffect, useState } from "react";
import { fmtError, fmtEUR, fmtDate } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { Loader2, Plus, Copy, Check, Gift } from "lucide-react";

export default function FidelityGiftPage() {
  const { api } = useTenant();
  const [items, setItems] = useState(null);
  const [amount, setAmount] = useState(50);
  const [note, setNote] = useState("");
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    if (!api) return;
    api.listGiftcards().then(setItems).catch((e) => setError(fmtError(e)));
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError("");
    try {
      await api.createGiftcard(parseFloat(amount), note || null);
      setAmount(50);
      setNote("");
      await load();
    } catch (err) {
      setError(fmtError(err));
    } finally {
      setCreating(false);
    }
  };

  const copy = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(code);
      setTimeout(() => setCopied(null), 1500);
    } catch (e) { console.error("clipboard copy failed", e); }
  };

  if (!items) return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-[#A78BFA]" /></div>;

  return (
    <div data-testid="giftcards-page" className="max-w-5xl">
      <div className="text-[11px] tracking-[0.3em] uppercase text-[#A78BFA] mb-3">Fidelity & gift</div>
      <h1 className="font-serif font-light text-4xl md:text-5xl tracking-tight text-[#1A1A1A]">
        Genera <span className="italic">gift card</span>.
      </h1>
      <p className="mt-4 text-[#4A4A4A] font-light max-w-2xl">
        Crea un codice univoco da regalare a una cliente: una volta riscattato, l'importo va sul suo wallet AURA.
      </p>

      <form onSubmit={create} className="mt-8 rounded-3xl border border-black/5 bg-white p-6 grid sm:grid-cols-5 gap-4 items-end" data-testid="giftcard-form">
        <label className="block sm:col-span-1">
          <span className="block text-[10px] tracking-[0.25em] uppercase text-[#737373] mb-2">Importo €</span>
          <input
            data-testid="giftcard-amount"
            type="number"
            min="1"
            step="1"
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full bg-[#FAFAFA] border border-black/10 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:border-[#A78BFA]"
          />
        </label>
        <label className="block sm:col-span-3">
          <span className="block text-[10px] tracking-[0.25em] uppercase text-[#737373] mb-2">Nota (opzionale)</span>
          <input
            data-testid="giftcard-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Es. Regalo compleanno Maria"
            className="w-full bg-[#FAFAFA] border border-black/10 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:border-[#A78BFA]"
          />
        </label>
        <button
          type="submit"
          data-testid="giftcard-create"
          disabled={creating}
          className="sm:col-span-1 inline-flex items-center justify-center gap-2 bg-[#1A1A1A] hover:bg-[#333] disabled:opacity-50 text-white rounded-full px-5 py-3 text-sm"
        >
          {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          Crea
        </button>
      </form>

      {error && <div className="mt-4 text-sm text-[#B91C1C]">{error}</div>}

      <div className="mt-10 rounded-3xl border border-black/5 bg-white overflow-hidden" data-testid="giftcards-list">
        {items.length === 0 ? (
          <div className="p-10 text-center text-[#737373] font-light">Ancora nessuna gift card generata.</div>
        ) : (
          <ul className="divide-y divide-black/5">
            {items.map((g) => (
              <li key={g.id} data-testid={`gift-${g.id}`} className="px-6 py-4 flex items-center gap-4 flex-wrap">
                <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-[#A78BFA] to-[#67E8F9] text-white flex items-center justify-center">
                  <Gift size={18} />
                </div>
                <div className="flex-1 min-w-[200px]">
                  <div className="font-mono text-sm tracking-wider text-[#1A1A1A]">{g.code}</div>
                  <div className="text-xs text-[#737373]">
                    {fmtEUR(g.initial_amount)} · creata {fmtDate(g.created_at)}
                    {g.note ? ` · ${g.note}` : ""}
                  </div>
                </div>
                <div className="text-right">
                  {g.redeemed_by ? (
                    <span className="inline-block text-[10px] tracking-[0.2em] uppercase bg-[#D1FAE5] text-[#065F46] border border-[#A7F3D0] rounded-full px-2 py-0.5">
                      Riscattata
                    </span>
                  ) : (
                    <span className="inline-block text-[10px] tracking-[0.2em] uppercase bg-[#F5F0FF] text-[#7C3AED] border border-[#E9D5FF] rounded-full px-2 py-0.5">
                      Attiva
                    </span>
                  )}
                </div>
                {!g.redeemed_by && (
                  <button
                    data-testid={`copy-${g.id}`}
                    onClick={() => copy(g.code)}
                    className="inline-flex items-center gap-1 text-xs bg-white border border-black/10 text-[#1A1A1A] rounded-full px-3 py-1.5 hover:bg-[#FAFAFA]"
                  >
                    {copied === g.code ? <Check size={12} /> : <Copy size={12} />}
                    {copied === g.code ? "Copiato" : "Copia"}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
